/**
 * 規劃器狀態 Store
 *
 * 管理座位規劃的核心狀態，包括：
 * - 圖片和規劃結果
 * - 家具物件
 * - 門標註
 * - 房間資訊
 * - 儲存的方案
 */

import { createStore } from './index';
import { DEFAULT_DIMENSIONS } from '@/config/constants';
import type { Point, Door, DoorClearanceZone, Pillar, Direction } from '@/types';
import type {
  FurnitureObject,
  CorridorZone,
  Aisle,
  OfficeArea,
  ReservedArea,
  RoomInfo,
} from '@/types/furniture';
import type { PlannerResponse, PlannerSettings, SavedPlanListItem, SinglePlan } from '@/types/api';

// ========================================================================
// 狀態型別
// ========================================================================

export interface PlannerState {
  // === 圖片相關 ===
  /** 規劃用的圖片檔案 */
  imageFile: File | null;

  /** 規劃用的圖片 Data URL */
  imageSrc: string | null;

  /** 原始圖片檔案（預處理前） */
  originalImageFile: File | null;

  /** 原始圖片 Data URL */
  originalImageSrc: string | null;

  // === 門標註 ===
  /** 標註的門列表 */
  doors: Door[];

  /** 門淨空區域 */
  doorClearanceZones: DoorClearanceZone[];

  // === 規劃設定 ===
  /** 規劃設定 */
  settings: PlannerSettings;

  /** 窗戶方向 */
  windowDirection: Direction | 'auto';

  // === 規劃結果 ===
  /** 最後一次 API 回應 */
  lastResponse: PlannerResponse | null;

  /** 家具物件列表 */
  furnitureObjects: FurnitureObject[];

  /** 原始家具物件列表（用於重設） */
  originalFurnitureObjects: FurnitureObject[];

  /** 房間多邊形 */
  roomPolygon: Point[] | null;

  /** 房間資訊 */
  roomInfo: RoomInfo | null;

  /** 走道區域 */
  corridorZones: CorridorZone[];

  /** 走道 */
  aisles: Aisle[];

  /** 辦公區域 */
  officeAreas: OfficeArea[];

  /** 保留區域 */
  reservedAreas: ReservedArea[];

  /** 柱子 */
  pillars: Pillar[];

  // === 互動狀態 ===
  /** 選取的家具物件 */
  selectedObject: FurnitureObject | null;

  /** 是否正在拖曳 */
  isDragging: boolean;

  /** 是否正在旋轉 */
  isRotating: boolean;

  /** Canvas 縮放比例 */
  canvasScale: number;

  // === 儲存方案 ===
  /** 所有儲存的方案 */
  savedPlans: SavedPlanListItem[];

  /** 當前方案索引 */
  currentPlanIndex: number;

  /** 當前載入的方案 ID */
  loadedPlanId: string | null;

  /** 當前載入的方案名稱 */
  currentPlanName: string | null;

  // === 處理狀態 ===
  /** 是否正在規劃 */
  isPlanning: boolean;

  /** 規劃進度 */
  planningProgress: number;

  /** 規劃訊息 */
  planningMessage: string;
}

// ========================================================================
// 預設設定
// ========================================================================

const defaultSettings: PlannerSettings = {
  deskWidth: DEFAULT_DIMENSIONS.DESK_WIDTH,
  deskHeight: DEFAULT_DIMENSIONS.DESK_HEIGHT,
  chairDepth: DEFAULT_DIMENSIONS.CHAIR_DEPTH,
  peopleDensity: DEFAULT_DIMENSIONS.PEOPLE_DENSITY,
  windowDirection: 'north',

  // 辦公桌數量設定
  deskCountMode: 'max',
  customDeskCount: undefined,

  // 家具擺放順序
  furnitureOrder: [
    { id: 'supervisor', label: '主管室', order: 0 },
    { id: 'large_meeting', label: '大會議室', order: 1 },
    { id: 'medium_meeting', label: '中會議室', order: 2 },
    { id: 'small_meeting', label: '小會議室', order: 3 },
    { id: 'desk', label: '辦公桌椅', order: 4 },
  ],

  supervisorRoomCount: 1,
  supervisorRoomWidth: DEFAULT_DIMENSIONS.SUPERVISOR_ROOM_WIDTH,
  supervisorRoomDepth: DEFAULT_DIMENSIONS.SUPERVISOR_ROOM_DEPTH,

  largeMeetingRoomCount: 1,
  largeMeetingRoomWidth: DEFAULT_DIMENSIONS.LARGE_MEETING_ROOM_WIDTH,
  largeMeetingRoomDepth: DEFAULT_DIMENSIONS.LARGE_MEETING_ROOM_DEPTH,

  mediumMeetingRoomCount: 1,
  mediumMeetingRoomWidth: DEFAULT_DIMENSIONS.MEDIUM_MEETING_ROOM_WIDTH,
  mediumMeetingRoomDepth: DEFAULT_DIMENSIONS.MEDIUM_MEETING_ROOM_DEPTH,

  smallMeetingRoomCount: 1,
  smallMeetingRoomWidth: DEFAULT_DIMENSIONS.SMALL_MEETING_ROOM_WIDTH,
  smallMeetingRoomDepth: DEFAULT_DIMENSIONS.SMALL_MEETING_ROOM_DEPTH,

  mainDoorClearance: 1.5,
  normalDoorClearance: 1.2,
  emergencyDoorClearance: 2.0,
};

// ========================================================================
// 初始狀態
// ========================================================================

const initialState: PlannerState = {
  // 圖片
  imageFile: null,
  imageSrc: null,
  originalImageFile: null,
  originalImageSrc: null,

  // 門標註
  doors: [],
  doorClearanceZones: [],

  // 設定
  settings: { ...defaultSettings },
  windowDirection: 'north',

  // 結果
  lastResponse: null,
  furnitureObjects: [],
  originalFurnitureObjects: [],
  roomPolygon: null,
  roomInfo: null,
  corridorZones: [],
  aisles: [],
  officeAreas: [],
  reservedAreas: [],
  pillars: [],

  // 互動
  selectedObject: null,
  isDragging: false,
  isRotating: false,
  canvasScale: 1,

  // 儲存方案
  savedPlans: [],
  currentPlanIndex: -1,
  loadedPlanId: null,
  currentPlanName: null,

  // 處理狀態
  isPlanning: false,
  planningProgress: 0,
  planningMessage: '',
};

// ========================================================================
// Store 實例
// ========================================================================

export const plannerStore = createStore<PlannerState>(initialState);

// ========================================================================
// Actions - 門標註
// ========================================================================

/**
 * 設定門列表
 */
export function setDoors(doors: Door[]): void {
  plannerStore.setState({ doors });
}

// ========================================================================
// Actions - 設定
// ========================================================================

/**
 * 更新規劃設定
 */
export function updateSettings(partial: Partial<PlannerSettings>): void {
  plannerStore.setState((state) => ({
    settings: { ...state.settings, ...partial },
  }));
}

// ========================================================================
// Actions - 規劃結果
// ========================================================================

/**
 * 將 API 家具項目轉換為前端家具物件
 * 注意：API 回傳的 position.x, y 是物件的左上角座標，需要轉換為中心點
 */
function transformApiFurnitureItem(item: FurnitureObject, _pixelToMeter: number): FurnitureObject {
  // API 回傳格式與前端格式不同，需要轉換
  const apiItem = item as unknown as {
    id: number;
    type: string;
    name: string;
    position: { x: number; y: number };
    dimensions: { width_pixels: number; height_pixels: number };
    direction: string;
    rotation: number;
    doors?: Array<{ start?: { x: number; y: number }; end?: { x: number; y: number }; startX?: number; startY?: number; endX?: number; endY?: number }>;
    desk?: { width_meters: number; depth_meters: number };
    chair?: { depth_meters: number };
    interior_furniture?: Array<{ type: string; x: number; y: number; width: number; height: number; rotation?: number }>;
  };

  const width = apiItem.dimensions.width_pixels;
  const height = apiItem.dimensions.height_pixels;
  // 注意：API 回傳的 position.x, y 是物件的左上角座標，需要加上一半的寬高來得到中心點
  // 這與 HTML 版本的處理方式一致
  const x = apiItem.position.x + width / 2;
  const y = apiItem.position.y + height / 2;
  const rotation = apiItem.rotation ?? 0;
  const rotationDegrees = (rotation * 180) / Math.PI;

  const base = {
    id: String(apiItem.id),
    name: apiItem.name,
    x,      // 中心點座標
    y,      // 中心點座標
    width,
    height,
    rotation,
    rotationDegrees,
    direction: apiItem.direction as Direction,
    originalX: x,
    originalY: y,
    originalRotation: rotation,
    color: '#cccccc',
  };

  // 將門的全域座標轉換為相對於物件中心的本地座標
  // API 返回的門座標是全域座標，但 Canvas 繪製時會先 translate 到物件中心
  // 因此需要將門座標轉換為相對於物件中心的本地座標
  const transformDoors = (doors: typeof apiItem.doors) => {
    if (!doors) return undefined;
    return doors.map((door) => {
      if (door.start && door.end) {
        return {
          start: {
            x: door.start.x - x,
            y: door.start.y - y,
          },
          end: {
            x: door.end.x - x,
            y: door.end.y - y,
          },
        };
      } else if (door.startX !== undefined) {
        return {
          start: {
            x: door.startX - x,
            y: (door.startY ?? 0) - y,
          },
          end: {
            x: (door.endX ?? 0) - x,
            y: (door.endY ?? 0) - y,
          },
        };
      }
      return door;
    });
  };

  // 根據類型返回不同的物件
  if (apiItem.type === 'desk_chair_pair') {
    // 保留 API 回傳的 desk 和 chair 深度資訊，供繪製時計算位置
    return {
      ...base,
      type: 'desk_chair_pair',
      desk: {
        width_meters: apiItem.desk?.width_meters ?? 1.2,
        depth_meters: apiItem.desk?.depth_meters ?? 0.6,
      },
      chair: {
        depth_meters: apiItem.chair?.depth_meters ?? 0.5,
      },
    } as FurnitureObject;
  } else if (apiItem.type === 'meeting_room') {
    return {
      ...base,
      type: 'meeting_room',
      doors: transformDoors(apiItem.doors),
      interior_furniture: apiItem.interior_furniture?.map((f) => ({
        type: f.type as 'table' | 'chair',
        x: f.x,
        y: f.y,
        width: f.width,
        height: f.height,
        rotation: f.rotation,
      })),
    } as FurnitureObject;
  } else {
    // supervisor_room
    return {
      ...base,
      type: 'supervisor_room',
      doors: transformDoors(apiItem.doors),
      interior_furniture: apiItem.interior_furniture?.map((f) => ({
        type: f.type as 'table' | 'chair',
        x: f.x,
        y: f.y,
        width: f.width,
        height: f.height,
        rotation: f.rotation,
      })),
    } as FurnitureObject;
  }
}

/** API 頂點資料型別 */
interface ApiVerticesData {
  vertices?: number[][];
}

/** API 門淨空區域資料型別 */
interface ApiDoorClearanceZone extends ApiVerticesData {
  door_type?: string;
}

/** API 走道資料型別 */
interface ApiAisleData extends ApiVerticesData {
  width?: number;
}

/**
 * 將 API 頂點陣列轉換為 Point 陣列
 */
function transformVertices(vertices?: number[][]): Point[] {
  return vertices?.map((v) => ({ x: v[0] ?? 0, y: v[1] ?? 0 })) ?? [];
}

/**
 * 轉換單一方案的資料為前端格式
 */
function transformPlanData(plan: SinglePlan | undefined, pixelToMeter: number): {
  furniture: FurnitureObject[];
  doorClearanceZones: DoorClearanceZone[];
  corridorZones: CorridorZone[];
  aisles: Aisle[];
  roomPolygon: Point[] | null;
  officeAreas: OfficeArea[];
  reservedAreas: ReservedArea[];
} {
  const apiItems = plan?.furniture_plan?.items ?? [];
  const furniture = apiItems.map((item) => transformApiFurnitureItem(item, pixelToMeter));

  const doorClearanceZones: DoorClearanceZone[] =
    plan?.door_clearance_zones?.map((zone) => ({
      points: transformVertices((zone as unknown as ApiDoorClearanceZone).vertices),
      doorType: (zone as unknown as ApiDoorClearanceZone).door_type as DoorClearanceZone['doorType'],
    })) ?? [];

  const corridorZones: CorridorZone[] =
    plan?.corridor_zones?.map((zone) => ({
      points: transformVertices((zone as unknown as ApiVerticesData).vertices),
      type: 'corridor' as const,
    })) ?? [];

  const aisles: Aisle[] =
    plan?.aisles?.map((aisle) => ({
      points: transformVertices((aisle as unknown as ApiAisleData).vertices),
      width: (aisle as unknown as ApiAisleData).width ?? 0,
    })) ?? [];

  const roomPolygon = plan?.room_polygon?.coordinates?.[0]?.map((coord) => ({
    x: coord[0] ?? 0,
    y: coord[1] ?? 0,
  })) ?? null;

  return {
    furniture,
    doorClearanceZones,
    corridorZones,
    aisles,
    roomPolygon,
    officeAreas: plan?.office_areas ?? [],
    reservedAreas: plan?.reserved_areas ?? [],
  };
}

/**
 * 設定規劃結果
 */
export function setPlannerResult(response: PlannerResponse): void {
  const plan = response.data?.plans?.[0];
  const apiRoomInfo = response.data?.room_info;
  const pixelToMeter = apiRoomInfo?.pixel_to_meter_ratio ?? 1;

  const planData = transformPlanData(plan, pixelToMeter);

  const roomInfo: RoomInfo | null = apiRoomInfo
    ? {
        width: apiRoomInfo.dimensions.width / apiRoomInfo.pixel_to_meter_ratio,
        height: apiRoomInfo.dimensions.height / apiRoomInfo.pixel_to_meter_ratio,
        area: apiRoomInfo.area_square_meters,
        areaPing: apiRoomInfo.area_ping,
        pixelToMeter: apiRoomInfo.pixel_to_meter_ratio,
      }
    : null;

  const pillars: Pillar[] =
    apiRoomInfo?.pillars?.map((p) => ({
      x: p.center[0] - p.size[0] / 2,
      y: p.center[1] - p.size[1] / 2,
      width: p.size[0],
      height: p.size[1],
      type: 'detected' as const,
    })) ?? [];

  plannerStore.setState({
    lastResponse: response,
    furnitureObjects: planData.furniture,
    originalFurnitureObjects: [...planData.furniture],
    roomPolygon: planData.roomPolygon,
    roomInfo,
    doorClearanceZones: planData.doorClearanceZones,
    corridorZones: planData.corridorZones,
    aisles: planData.aisles,
    officeAreas: planData.officeAreas,
    reservedAreas: planData.reservedAreas,
    pillars,
    isPlanning: false,
    planningProgress: 100,
    planningMessage: '規劃完成',
  });
}

/**
 * 載入單一方案到 store（用於切換方案時）
 */
export function loadSinglePlan(plan: SinglePlan): void {
  const state = plannerStore.getState();
  const pixelToMeter = state.lastResponse?.data?.room_info?.pixel_to_meter_ratio ?? 1;

  const planData = transformPlanData(plan, pixelToMeter);

  plannerStore.setState({
    furnitureObjects: planData.furniture,
    originalFurnitureObjects: [...planData.furniture],
    roomPolygon: planData.roomPolygon,
    doorClearanceZones: planData.doorClearanceZones,
    corridorZones: planData.corridorZones,
    aisles: planData.aisles,
    officeAreas: planData.officeAreas,
    reservedAreas: planData.reservedAreas,
  });
}

// ========================================================================
// Actions - 家具操作
// ========================================================================

/**
 * 選取家具物件
 */
export function selectFurniture(obj: FurnitureObject | null): void {
  plannerStore.setState({ selectedObject: obj });
}

/**
 * 更新家具位置
 */
export function updateFurniturePosition(id: string, x: number, y: number): void {
  plannerStore.setState((state) => ({
    furnitureObjects: state.furnitureObjects.map((obj) =>
      obj.id === id ? { ...obj, x, y } : obj
    ),
  }));
}

/**
 * 更新家具旋轉
 */
export function updateFurnitureRotation(id: string, rotation: number): void {
  const rotationDegrees = (rotation * 180) / Math.PI;
  plannerStore.setState((state) => ({
    furnitureObjects: state.furnitureObjects.map((obj) =>
      obj.id === id ? { ...obj, rotation, rotationDegrees } : obj
    ),
  }));
}

/**
 * 設定拖曳狀態
 */
export function setIsDragging(isDragging: boolean): void {
  plannerStore.setState({ isDragging });
}

/**
 * 設定旋轉狀態
 */
export function setIsRotating(isRotating: boolean): void {
  plannerStore.setState({ isRotating });
}

// ========================================================================
// Actions - 儲存方案管理
// ========================================================================

/**
 * 設定當前載入的規劃 ID
 */
export function setLoadedPlanId(planId: string | null): void {
  plannerStore.setState({ loadedPlanId: planId });
}

/**
 * 清除當前載入的規劃 ID
 */
export function clearLoadedPlanId(): void {
  plannerStore.setState({ loadedPlanId: null, currentPlanName: null });
}

/**
 * 取得當前載入的規劃 ID
 */
export function getLoadedPlanId(): string | null {
  return plannerStore.getState().loadedPlanId;
}

/**
 * 設定當前規劃名稱
 */
export function setCurrentPlanName(name: string): void {
  plannerStore.setState({ currentPlanName: name });
}

/**
 * 取得當前規劃名稱
 */
export function getCurrentPlanName(): string | null {
  return plannerStore.getState().currentPlanName;
}

/**
 * 重置所有規劃狀態
 * 用於登出時清除所有規劃資料
 */
export function resetPlannerState(): void {
  plannerStore.setState({
    // 圖片
    imageFile: null,
    imageSrc: null,
    originalImageFile: null,
    originalImageSrc: null,

    // 門標註
    doors: [],
    doorClearanceZones: [],

    // 設定（保留預設值）
    settings: { ...defaultSettings },
    windowDirection: 'north',

    // 結果
    lastResponse: null,
    furnitureObjects: [],
    originalFurnitureObjects: [],
    roomPolygon: null,
    roomInfo: null,
    corridorZones: [],
    aisles: [],
    officeAreas: [],
    reservedAreas: [],
    pillars: [],

    // 互動
    selectedObject: null,
    isDragging: false,
    isRotating: false,
    canvasScale: 1,

    // 儲存方案
    savedPlans: [],
    currentPlanIndex: -1,
    loadedPlanId: null,
    currentPlanName: null,

    // 處理狀態
    isPlanning: false,
    planningProgress: 0,
    planningMessage: '',
  });
}
